import '../../base/outline-container/outline-container';
import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, state } from 'lit/decorators.js';
import componentStyles from './idblab-section-wrapper.css.lit';
import { ClassInfo, classMap } from 'lit/directives/class-map.js';
import { SlotController } from '../../controllers/slot-controller';

/**
 * Define the available background colors and an
 * associated type definition.
 */
export const availableBackgrounds = [
  'gray',
  'green',
  'blue',
  'white',
  'transparent',
  'sm-yellow-wave',
  'md-yellow-wave',
  'lg-yellow-wave',
  'sm-green-wave',
  'md-green-wave',
  'lg-green-wave',
  'sm-blue-wave',
  'md-blue-wave',
  'lg-blue-wave',
  'idblab-light-blue',
  'idblab-blue',
  'idblab-magenta',
  'idblab-green',
  'idblab-yellow',
  'idblab-gray-light',
  'black',
];

export type SectionBackgrounds = typeof availableBackgrounds[number];

/**
 * The IDB Lab Section component
 * @element idblab-section-wrapper
 * @slot eyebrow - The eyebrow slot.
 * @slot title - The title slot.
 * @slot content - The primary content slot.
 * @slot cta - The CTA slot.
 * @see https://yoksel.github.io/url-encoder/ to encode wave backgrounds
 * @todo update the slots to template partials.
 */
@customElement('idblab-section-wrapper')
export class IdbLabSection extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotController(
    this, // This, the host element.
    true // To shift or not to shift LightDom nodes to ShadowDOM.
  );
  /**
   * The background of the section.
   */
  @property({ type: String, attribute: 'section-background' })
  sectionBackground: SectionBackgrounds;

  @state()
  hasEyebrowSlot: boolean;
  @state() hasTitleSlot: boolean;
  @state() hasContentSlot: boolean;
  @state() hasCtaSlot: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.hasEyebrowSlot = this.slots.test('eyebrow');
    this.hasTitleSlot = this.slots.test('title');
    this.hasContentSlot = this.slots.test('content');
    this.hasCtaSlot = this.slots.test('cta');
  }

  firstUpdated(): void {}

  render(): TemplateResult {
    const sectionClasses = {
      'idb-section-wrapper': true,
      [`section-background--${this.sectionBackground}`]: this.sectionBackground,
    };
    const sectionHeaderClasses = {
      'section-header': true,
    };
    const sectionContentClasses = {
      'section-content': true,
    };

    return html`
      <section class="${classMap(sectionClasses)}">
        ${this.sectionHeaderTemplate(sectionHeaderClasses)}
        ${this.sectionContentTemplate(sectionContentClasses)}
      </section>
    `;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  sectionHeaderTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasEyebrowSlot && !this.hasTitleSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.eyebrowTemplate()}
      ${this.titleTemplate()}
    </section>`;
  }

  /**
   * Template partial for the Eyebrow slot.
   * @returns TemplateResult | null
   */
  eyebrowTemplate(): TemplateResult | null {
    if (!this.hasEyebrowSlot) return null;

    return html`<slot name="eyebrow"></slot>`;
  }

  /**
   * Template partial for the Title slot.
   * @returns TemplateResult | null
   */
  titleTemplate(): TemplateResult | null {
    if (!this.hasTitleSlot) return null;

    return html`<slot name="title"></slot>`;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  sectionContentTemplate(classes: ClassInfo): TemplateResult | null {
    if (!this.hasContentSlot && !this.hasCtaSlot) return null;

    return html` <section class="${classMap(classes)}">
      ${this.contentTemplate()}
      ${this.ctaTemplate()}
    </section>`;
  }

  /**
   * Template partial for the Content slot.
   * @param classes
   * @returns TemplateResult | null
   */
  contentTemplate(): TemplateResult | null {
    if (!this.hasContentSlot) return null;

    return html` <section class="content-wrapper">
      <outline-container x-padding><slot name="content"></slot></outline-container>
    </section>`;
  }

  /**
   * Template partial for the CTA slot.
   * @param classes
   * @returns TemplateResult | null
   */
  ctaTemplate(): TemplateResult | null {
    if (!this.hasCtaSlot) return null;

    return html` <section class="cta-wrapper">
      <slot name="cta"></slot>
    </section>`;
  }
}
